import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import debounce from 'lodash.debounce';
import Navbar from '../components/Navbar';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '../components/ui/sheet';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  MapPin,
  List,
  Map as MapIcon,
  SlidersHorizontal,
  X,
  Loader2,
  RefreshCw,
  CheckCircle2,
  Navigation,
  Locate,
  Clock,
  Trash2
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { filterDemoProperties } from '../lib/demoData';
import { formatOriginalPrice, formatPriceWithConversion, formatMarkerPrice, getCurrencyInfo, getSupportedCurrencies } from '../utils/currency';
import {
  getCurrentPosition,
  checkGeolocationPermission,
  getStoredLocationPreference,
  setStoredLocationPreference,
  calculateDistance,
  formatDistance,
  getBoundsForRadius,
  getDefaultLocationByTimezone,
  RADIUS_OPTIONS,
} from '../utils/geolocation';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const hasValidApiKey = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY !== 'YOUR_GOOGLE_MAPS_API_KEY';

const libraries = ['places'];

const mapContainerStyle = { width: '100%', height: '100%' };

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  gestureHandling: 'greedy',
  styles: [
    { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  ],
};

const propertyTypes = [
  { value: 'all', label: 'All Types' },
  { value: 'room', label: 'Room' },
  { value: 'flat', label: 'Flat' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'house', label: 'House' },
  { value: 'villa', label: 'Villa' },
  { value: 'studio', label: 'Studio' },
  { value: 'office', label: 'Office' },
  { value: 'commercial', label: 'Commercial' },
];

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'popular', label: 'Most Popular' },
  { value: 'nearest', label: 'Nearest to Me' },
];

// Recent searches storage
const RECENT_SEARCHES_KEY = 'roomhora_recent_searches';

function getRecentSearches() {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}

function addRecentSearch(search) {
  try {
    const recent = getRecentSearches().filter(s => s.text !== search.text);
    recent.unshift(search);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, 5)));
  } catch { /* ignore */ }
}

function clearRecentSearches() {
  try { localStorage.removeItem(RECENT_SEARCHES_KEY); } catch { /* ignore */ }
}

// Location Permission Dialog
function LocationPermissionDialog({ open, onClose, onAllow, onDeny }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl md:text-3xl lg:text-3xl">Find properties near you</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center">
              <Locate className="w-10 h-10 text-accent" />
            </div>
          </div>
          <p className="text-slate-600 text-center mb-6">
            Allow RoomHora to use your location to show nearby properties, calculate distances, and improve your search experience.
          </p>
          <div className="space-y-3">
            <Button onClick={onAllow} className="w-full h-12 bg-accent hover:bg-accent/90 rounded-xl" data-testid="allow-location-btn">
              <Navigation className="w-4 h-4 mr-2" />
              Use My Location
            </Button>
            <Button onClick={onDeny} variant="outline" className="w-full h-12 rounded-xl" data-testid="deny-location-btn">
              Search Manually
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Map Placeholder Fallback (when no API key)
function MapPlaceholder({ markers, onMarkerClick, selectedProperty, hoveredProperty }) {
  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
      <div className="absolute inset-0" style={{
        backgroundImage: 'linear-gradient(rgba(148,163,184,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.15) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />
      <div className="absolute top-1/3 left-0 right-0 h-px bg-slate-300/50" />
      <div className="absolute top-2/3 left-0 right-0 h-px bg-slate-300/50" />
      <div className="absolute left-1/3 top-0 bottom-0 w-px bg-slate-300/50" />
      <div className="absolute left-2/3 top-0 bottom-0 w-px bg-slate-300/50" />
      {markers.slice(0, 20).map((marker, idx) => {
        const x = 10 + (idx % 5) * 18 + (idx * 7 % 10);
        const y = 10 + Math.floor(idx / 5) * 20 + (idx * 3 % 15);
        const isActive = selectedProperty === marker.property_id || hoveredProperty === marker.property_id;
        return (
          <button
            key={marker.property_id}
            onClick={() => onMarkerClick(marker)}
            className={`absolute px-2 py-1 rounded-full text-xs font-bold shadow-md transition-all cursor-pointer ${
              isActive ? 'bg-accent text-white scale-110 z-10' : 'bg-white text-slate-800 hover:bg-accent hover:text-white hover:scale-105'
            }`}
            style={{ left: `${x}%`, top: `${y}%` }}
            data-testid={`placeholder-marker-${marker.property_id}`}
          >
            {formatMarkerPrice(marker.monthly_rent, marker.currency)}
          </button>
        );
      })}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg text-center max-w-sm pointer-events-auto">
          <MapIcon className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <h3 className="font-serif text-lg md:text-xl lg:text-xl text-slate-800 mb-1">Interactive Map</h3>
          <p className="text-slate-500 text-sm md:text-base lg:text-base mb-3">
            Add a Google Maps API key to enable the full interactive map experience.
          </p>
          {markers.length > 0 && (
            <p className="text-xs md:text-sm lg:text-sm text-slate-400">{markers.length} properties in this area</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Custom Price Marker overlay for the map
function PriceMarkerOverlay({ position, text, isActive, onClick, propertyId, verified }) {
  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(width, height) => ({
        x: -(width / 2),
        y: -(height + 8),
      })}
    >
      <div
        onClick={onClick}
        className={`cursor-pointer select-none transition-all duration-150 ${isActive ? 'z-50 scale-110' : 'z-10 hover:z-50 hover:scale-105'}`}
        data-testid={`map-marker-${propertyId}`}
      >
        <div className={`px-3 py-1.5 rounded-full font-bold text-xs shadow-lg whitespace-nowrap border ${
          isActive
            ? 'bg-accent text-white border-accent shadow-accent/30'
            : 'bg-white text-slate-900 border-slate-200 hover:border-accent hover:text-accent'
        }`}>
          {text}
          {verified && <CheckCircle2 className="inline w-3 h-3 ml-1 opacity-80" />}
        </div>
        {/* Arrow */}
        <div className="flex justify-center -mt-px">
          <div className={`w-2 h-2 rotate-45 ${isActive ? 'bg-accent' : 'bg-white border-r border-b border-slate-200'}`} />
        </div>
      </div>
    </OverlayView>
  );
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { viewerCurrency, setViewerCurrency, supportedCurrencies } = useCurrency();

  const [properties, setProperties] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backendOffline, setBackendOffline] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [viewMode, setViewMode] = useState('split');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapCenter, setMapCenter] = useState(getDefaultLocationByTimezone());
  const [mapZoom, setMapZoom] = useState(12);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showSearchArea, setShowSearchArea] = useState(false);

  // User location state
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  // Search state
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [recentSearches, setRecentSearches] = useState(getRecentSearches());
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [placePredictions, setPlacePredictions] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    country: searchParams.get('country') || '',
    propertyType: searchParams.get('type') || 'all',
    minRent: parseInt(searchParams.get('minRent')) || 0,
    maxRent: parseInt(searchParams.get('maxRent')) || 50000,
    verifiedOnly: searchParams.get('verified') === 'true',
    sortBy: searchParams.get('sort') || 'newest',
    nearbyRadius: parseInt(searchParams.get('radius')) || 0,
    facilities: searchParams.get('facilities')?.split(',').filter(Boolean) || [],
    tenantType: searchParams.get('tenantType') || '',
  });

  const mapRef = useRef(null);
  const clustererRef = useRef(null);
  const nativeMarkersRef = useRef([]);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isUserMapAction = useRef(false);

  // Load Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: hasValidApiKey ? GOOGLE_MAPS_API_KEY : '',
    libraries,
    preventGoogleFontsLoading: !hasValidApiKey,
  });

  // Initialize Places services when map loads
  useEffect(() => {
    if (isLoaded && window.google?.maps?.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  // Check location permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      const stored = getStoredLocationPreference();
      if (stored === 'granted') {
        try {
          const pos = await getCurrentPosition();
          setUserLocation(pos);
          setLocationPermission('granted');
          setMapCenter({ lat: pos.latitude, lng: pos.longitude });
        } catch {
          setStoredLocationPreference(null);
        }
      } else if (stored === 'denied') {
        setLocationPermission('denied');
      } else {
        const permission = await checkGeolocationPermission();
        if (permission === 'granted') {
          try {
            const pos = await getCurrentPosition();
            setUserLocation(pos);
            setLocationPermission('granted');
            setStoredLocationPreference('granted');
            setMapCenter({ lat: pos.latitude, lng: pos.longitude });
          } catch { /* silent */ }
        } else if (permission === 'prompt') {
          setTimeout(() => setShowLocationDialog(true), 1500);
        }
      }
    };
    checkPermission();
  }, []);

  const handleAllowLocation = async () => {
    setShowLocationDialog(false);
    setLocationLoading(true);
    try {
      const pos = await getCurrentPosition();
      setUserLocation(pos);
      setLocationPermission('granted');
      setStoredLocationPreference('granted');
      setMapCenter({ lat: pos.latitude, lng: pos.longitude });
      setMapZoom(14);
      toast.success('Location enabled! Showing nearby properties.');
    } catch (error) {
      toast.error(error.message);
      setLocationPermission('denied');
      setStoredLocationPreference('denied');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleDenyLocation = () => {
    setShowLocationDialog(false);
    setLocationPermission('denied');
    setStoredLocationPreference('denied');
  };

  const handleCenterOnUser = async () => {
    if (userLocation) {
      const center = { lat: userLocation.latitude, lng: userLocation.longitude };
      setMapCenter(center);
      setMapZoom(14);
      mapRef.current?.panTo(center);
    } else {
      setLocationLoading(true);
      try {
        const pos = await getCurrentPosition();
        setUserLocation(pos);
        setLocationPermission('granted');
        setStoredLocationPreference('granted');
        const center = { lat: pos.latitude, lng: pos.longitude };
        setMapCenter(center);
        setMapZoom(14);
        mapRef.current?.panTo(center);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLocationLoading(false);
      }
    }
  };

  // Search autocomplete - Google Places first, server fallback
  const handleSearchChange = (value) => {
    setSearchInput(value);
    setShowRecentSearches(value.length === 0);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        if (autocompleteService.current && hasValidApiKey) {
          autocompleteService.current.getPlacePredictions(
            { input: value, types: ['geocode', 'establishment'] },
            (predictions, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                setPlacePredictions(predictions.slice(0, 6).map(p => ({ ...p, source: 'google' })));
              } else {
                fetchServerSuggestions(value);
              }
            }
          );
        } else {
          fetchServerSuggestions(value);
        }
      }, 250);
    } else {
      setPlacePredictions([]);
    }
  };

  const fetchServerSuggestions = async (value) => {
    try {
      const res = await fetch(`${API_URL}/api/search/suggestions?q=${encodeURIComponent(value)}`);
      if (res.ok) {
        const data = await res.json();
        setPlacePredictions((data.suggestions || []).map(s => ({
          description: s.value,
          structured_formatting: { main_text: s.value, secondary_text: s.type },
          place_id: null,
          source: 'server',
        })));
      }
    } catch {
      setPlacePredictions([]);
    }
  };

  // Select a place from autocomplete
  const handleSelectPlace = (prediction) => {
    setSearchInput(prediction.description);
    setPlacePredictions([]);
    setShowRecentSearches(false);

    addRecentSearch({ text: prediction.description, placeId: prediction.place_id });
    setRecentSearches(getRecentSearches());

    if (!prediction.place_id || prediction.source === 'server') {
      setFilters(prev => ({ ...prev, city: prediction.description }));
      
      // Geocode the location to move the map
      const geocodeAndPan = async () => {
        // Try Google Geocoder first
        if (isLoaded && window.google?.maps) {
          try {
            const geocoder = new window.google.maps.Geocoder();
            const result = await new Promise((resolve, reject) => {
              geocoder.geocode({ address: prediction.description }, (results, status) => {
                if (status === 'OK' && results[0]) resolve(results[0]);
                else reject(new Error(status));
              });
            });
            const loc = result.geometry.location;
            const lat = loc.lat();
            const lng = loc.lng();
            isUserMapAction.current = false;
            setMapCenter({ lat, lng });
            setMapZoom(12);
            mapRef.current?.panTo({ lat, lng });
            mapRef.current?.setZoom(12);
            return;
          } catch {
            // Fall through to server geocode
          }
        }
        
        // Fallback: server-side geocoding from property data
        try {
          const res = await fetch(`${API_URL}/api/geocode?q=${encodeURIComponent(prediction.description)}`);
          if (res.ok) {
            const data = await res.json();
            if (data.found) {
              isUserMapAction.current = false;
              setMapCenter({ lat: data.latitude, lng: data.longitude });
              setMapZoom(data.zoom);
              if (mapRef.current) {
                mapRef.current.panTo({ lat: data.latitude, lng: data.longitude });
                mapRef.current.setZoom(data.zoom);
              }
            }
          }
        } catch { /* silent */ }
      };
      
      geocodeAndPan();
      return;
    }

    if (!placesService.current && mapRef.current) {
      placesService.current = new window.google.maps.places.PlacesService(mapRef.current);
    }

    if (placesService.current) {
      placesService.current.getDetails(
        { placeId: prediction.place_id, fields: ['geometry', 'address_components', 'name'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            // Determine zoom based on place type
            let zoom = 13;
            const types = place.address_components?.flatMap(c => c.types) || [];
            if (types.includes('country')) zoom = 5;
            else if (types.includes('administrative_area_level_1')) zoom = 8;
            else if (types.includes('locality')) zoom = 12;
            else if (types.includes('sublocality') || types.includes('neighborhood')) zoom = 14;
            else if (types.includes('route') || types.includes('street_address')) zoom = 16;

            setMapCenter({ lat, lng });
            setMapZoom(zoom);
            mapRef.current?.panTo({ lat, lng });

            const cityComponent = place.address_components?.find(c =>
              c.types.includes('locality') || c.types.includes('administrative_area_level_1')
            );
            const countryComponent = place.address_components?.find(c => c.types.includes('country'));

            setFilters(prev => ({
              ...prev,
              city: cityComponent?.long_name || '',
              country: countryComponent?.long_name || '',
            }));
          }
        }
      );
    }
  };

  const handleSelectRecentSearch = (search) => {
    setSearchInput(search.text);
    setShowRecentSearches(false);
    if (search.placeId && placesService.current) {
      placesService.current.getDetails(
        { placeId: search.placeId, fields: ['geometry'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setMapCenter({ lat, lng });
            setMapZoom(13);
            mapRef.current?.panTo({ lat, lng });
          }
        }
      );
    }
  };

  // Fetch properties list
  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.country) params.append('country', filters.country);
      if (filters.propertyType && filters.propertyType !== 'all') params.append('property_type', filters.propertyType);
      if (filters.minRent > 0) params.append('min_rent', filters.minRent);
      if (filters.maxRent < 50000) params.append('max_rent', filters.maxRent);
      if (filters.verifiedOnly) params.append('verified_only', 'true');
      if (filters.facilities.length > 0) params.append('facilities', filters.facilities.join(','));
      if (filters.tenantType) params.append('tenant_type', filters.tenantType);
      params.append('sort_by', filters.sortBy);
      params.append('page', page);
      params.append('limit', 20);

      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/properties?${params}`, { headers });
      if (response.ok) {
        const data = await response.json();
        let fetchedProperties = data.properties || [];

        if (userLocation) {
          fetchedProperties = fetchedProperties.map(p => ({
            ...p,
            distance: p.location?.latitude && p.location?.longitude
              ? calculateDistance(userLocation.latitude, userLocation.longitude, p.location.latitude, p.location.longitude)
              : null,
          }));
          if (filters.sortBy === 'nearest') {
            fetchedProperties.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
          }
        }

        setProperties(fetchedProperties);
        setTotal(data.total || 0);
        setBackendOffline(false);
      } else {
        // Non-2xx from backend: treat as offline for UX purposes.
        throw new Error(`Backend returned ${response.status}`);
      }
    } catch (error) {
      // Backend unreachable → fall back to demo properties with current filters
      // applied so the page still shows meaningful results. We also surface a
      // banner so users aren't misled into thinking these are live listings.
      console.warn('Using demo properties (backend unreachable):', error.message);
      let demo = filterDemoProperties({
        city: filters.city,
        minPrice: filters.minRent > 0 ? filters.minRent : undefined,
        maxPrice: filters.maxRent < 50000 ? filters.maxRent : undefined,
      });
      if (userLocation) {
        demo = demo.map(p => ({
          ...p,
          distance: p.location?.latitude && p.location?.longitude
            ? calculateDistance(userLocation.latitude, userLocation.longitude, p.location.latitude, p.location.longitude)
            : null,
        }));
      }
      setProperties(demo);
      setTotal(demo.length);
      setBackendOffline(true);
    } finally {
      setLoading(false);
    }
  }, [filters, page, userLocation]);

  // Fetch map markers based on bounds
  const fetchMapMarkersRaw = useCallback(async (bounds) => {
    if (!bounds) return;
    setMapLoading(true);
    try {
      const params = new URLSearchParams({
        north: bounds.north.toString(),
        south: bounds.south.toString(),
        east: bounds.east.toString(),
        west: bounds.west.toString(),
      });
      if (filters.propertyType && filters.propertyType !== 'all') params.append('property_type', filters.propertyType);
      if (filters.minRent > 0) params.append('min_rent', filters.minRent.toString());
      if (filters.maxRent < 50000) params.append('max_rent', filters.maxRent.toString());
      if (filters.facilities.length > 0) params.append('facilities', filters.facilities.join(','));
      if (filters.tenantType) params.append('tenant_type', filters.tenantType);

      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/properties/map?${params}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setMarkers(data.markers || []);
      }
    } catch (error) {
      // Backend unreachable → synthesize markers from the demo property set.
      console.warn('Using demo markers (backend unreachable):', error.message);
      const demoMarkers = filterDemoProperties().map(p => ({
        property_id: p.property_id,
        latitude: p.location.latitude,
        longitude: p.location.longitude,
        monthly_rent: p.monthly_rent,
        currency: p.currency,
        title: p.title,
      }));
      setMarkers(demoMarkers);
    } finally {
      setMapLoading(false);
    }
  }, [filters.propertyType, filters.minRent, filters.maxRent, filters.facilities, filters.tenantType]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchMapMarkers = useMemo(() => debounce(fetchMapMarkersRaw, 400), [fetchMapMarkersRaw]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  // Fallback markers from property list when no map
  useEffect(() => {
    if (!mapBounds && !hasValidApiKey && properties.length > 0) {
      const fallbackMarkers = properties
        .filter(p => p.location?.latitude && p.location?.longitude)
        .map(p => ({
          property_id: p.property_id,
          latitude: p.location.latitude,
          longitude: p.location.longitude,
          monthly_rent: p.monthly_rent,
          currency: p.currency || 'USD',
          billing_period: p.billing_period || 'monthly',
          property_type: p.property_type,
          title: p.title,
          owner_verified: p.owner_verified || false,
        }));
      setMarkers(fallbackMarkers);
    }
  }, [properties, mapBounds]);

  useEffect(() => {
    if (mapBounds) {
      fetchMapMarkers(mapBounds);
    }
    return () => fetchMapMarkers.cancel?.();
  }, [mapBounds, fetchMapMarkers]);

  // --- Marker Clustering (imperative, for performance) ---
  useEffect(() => {
    if (!mapRef.current || !isLoaded || !hasValidApiKey) return;

    // Clear old native markers
    nativeMarkersRef.current.forEach(m => m.setMap(null));
    nativeMarkersRef.current = [];

    // Create native markers for clustering
    const gMarkers = markers.map((m) => {
      const marker = new window.google.maps.Marker({
        position: { lat: m.latitude, lng: m.longitude },
        // Invisible marker icon – we use OverlayView for visual price labels
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 0,
        },
        map: null, // managed by clusterer
      });
      marker._propertyId = m.property_id;
      return marker;
    });

    nativeMarkersRef.current = gMarkers;

    // Create or update clusterer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.addMarkers(gMarkers);
    } else {
      clustererRef.current = new MarkerClusterer({
        map: mapRef.current,
        markers: gMarkers,
        renderer: {
          render: ({ count, position }) => {
            return new window.google.maps.Marker({
              position,
              label: {
                text: String(count),
                color: '#fff',
                fontSize: '13px',
                fontWeight: 'bold',
              },
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 22 + Math.min(count, 50) * 0.3,
                fillColor: '#e11d48',
                fillOpacity: 0.9,
                strokeColor: '#fff',
                strokeWeight: 2,
              },
              zIndex: 1000 + count,
            });
          },
        },
      });
    }

    return () => {
      // Cleanup on unmount
    };
  }, [markers, isLoaded]);

  // Cleanup clusterer on unmount
  useEffect(() => {
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
        clustererRef.current = null;
      }
      nativeMarkersRef.current.forEach(m => m.setMap(null));
    };
  }, []);

  // Decide whether to show individual price markers or let clustering handle it
  const showIndividualMarkers = markers.length <= 60;

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (window.google?.maps?.places) {
      placesService.current = new window.google.maps.places.PlacesService(map);
    }
  }, []);

  const handleBoundsChanged = useCallback(() => {
    if (!mapRef.current) return;
    const bounds = mapRef.current.getBounds();
    if (bounds) {
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      setMapBounds({
        north: ne.lat(),
        south: sw.lat(),
        east: ne.lng(),
        west: sw.lng(),
      });
      // Show "Search this area" button when user pans/zooms
      if (isUserMapAction.current) {
        setShowSearchArea(true);
      }
      isUserMapAction.current = true;
    }
  }, []);

  const handleMarkerClick = (marker) => {
    setSelectedProperty(marker.property_id);
    setTimeout(() => {
      const el = document.getElementById(`property-${marker.property_id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      city: '', country: '', propertyType: 'all',
      minRent: 0, maxRent: 50000, verifiedOnly: false,
      sortBy: 'newest', nearbyRadius: 0,
      facilities: [], tenantType: '',
    });
    setPage(1);
    setSearchInput('');
    setSearchParams({});
  };

  const activeFiltersCount = [
    filters.city, filters.country,
    filters.propertyType !== 'all',
    filters.minRent > 0, filters.maxRent < 50000,
    filters.verifiedOnly, filters.nearbyRadius > 0,
    filters.facilities.length > 0, filters.tenantType !== '',
  ].filter(Boolean).length;

  const handleSearchThisArea = () => {
    if (mapBounds) {
      fetchMapMarkersRaw(mapBounds);
      setFilters(prev => ({ ...prev, city: '', country: '' }));
      setSearchInput('');
      fetchProperties();
      setShowSearchArea(false);
      toast.success('Searching this area...');
    }
  };

  const CurrencySelector = () => (
    <Select value={viewerCurrency} onValueChange={setViewerCurrency}>
      <SelectTrigger className="w-28 h-9 text-sm md:text-base lg:text-base">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {supportedCurrencies.slice(0, 20).map((c) => (
          <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Handle property card hover -> highlight marker
  const handlePropertyHover = (propertyId) => {
    setHoveredProperty(propertyId);
    // Pan to marker if in map or split view
    if (propertyId && (viewMode === 'split' || viewMode === 'map')) {
      const marker = markers.find(m => m.property_id === propertyId);
      if (marker && mapRef.current) {
        const bounds = mapRef.current.getBounds();
        const pos = new window.google.maps.LatLng(marker.latitude, marker.longitude);
        if (bounds && !bounds.contains(pos)) {
          mapRef.current.panTo(pos);
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="search-page">
      <Navbar />

      <LocationPermissionDialog
        open={showLocationDialog}
        onClose={() => setShowLocationDialog(false)}
        onAllow={handleAllowLocation}
        onDeny={handleDenyLocation}
      />

      {/* Search Header */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="rh-container py-3">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search Input with Autocomplete */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search city, neighborhood, or landmark..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => setShowRecentSearches(searchInput.length === 0)}
                onBlur={() => setTimeout(() => { setShowRecentSearches(false); setPlacePredictions([]); }, 200)}
                className="pl-12 h-11 rounded-full border-slate-200 text-base lg:text-base"
                data-testid="search-location-input"
              />

              {/* Autocomplete Dropdown */}
              {(placePredictions.length > 0 || (showRecentSearches && recentSearches.length > 0)) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50">
                  {showRecentSearches && recentSearches.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between px-4 py-2 bg-slate-50">
                        <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Recent Searches
                        </span>
                        <button onClick={() => { clearRecentSearches(); setRecentSearches([]); }} className="text-xs text-slate-400 hover:text-slate-600">Clear</button>
                      </div>
                      {recentSearches.map((search, i) => (
                        <button key={i} onClick={() => handleSelectRecentSearch(search)}
                          className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{search.text}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {placePredictions.length > 0 && (
                    <div>
                      {placePredictions.map((prediction, idx) => (
                        <button
                          key={prediction.place_id || `suggestion-${idx}`}
                          onClick={() => handleSelectPlace(prediction)}
                          className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 transition-colors"
                          data-testid={`search-suggestion-${idx}`}
                        >
                          <MapPin className="w-4 h-4 text-accent flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="text-slate-900 font-medium">
                              {prediction.structured_formatting?.main_text || prediction.description}
                            </span>
                            {prediction.structured_formatting?.secondary_text && (
                              <span className="text-slate-500 text-sm ml-2">
                                {prediction.structured_formatting.secondary_text}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Quick Filters */}
            <Select value={filters.propertyType} onValueChange={(v) => handleFilterChange('propertyType', v)}>
              <SelectTrigger className="w-36 h-11 rounded-full" data-testid="property-type-select">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                {propertyTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.sortBy} onValueChange={(v) => handleFilterChange('sortBy', v)}>
              <SelectTrigger className="w-40 h-11 rounded-full" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} disabled={opt.value === 'nearest' && !userLocation}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* More Filters */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="h-11 rounded-full gap-2" data-testid="more-filters-btn">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {activeFiltersCount > 0 && <Badge className="bg-accent text-white ml-1">{activeFiltersCount}</Badge>}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[400px]">
                <SheetHeader>
                  <SheetTitle className="font-serif text-xl lg:text-xl">Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <Label>Price Range</Label>
                      <CurrencySelector />
                    </div>
                    <div className="text-sm md:text-base lg:text-base text-slate-600 mb-2">
                      {getCurrencyInfo(viewerCurrency).symbol}{filters.minRent.toLocaleString()} - {getCurrencyInfo(viewerCurrency).symbol}{filters.maxRent.toLocaleString()}+
                    </div>
                    <Slider
                      value={[filters.minRent, filters.maxRent]}
                      onValueChange={([min, max]) => { handleFilterChange('minRent', min); handleFilterChange('maxRent', max); }}
                      min={0} max={50000} step={500} className="mt-2"
                    />
                  </div>
                  {userLocation && (
                    <div>
                      <Label className="mb-4 block">Search Radius</Label>
                      <Select value={filters.nearbyRadius.toString()} onValueChange={(v) => handleFilterChange('nearbyRadius', parseInt(v))}>
                        <SelectTrigger><SelectValue placeholder="Any distance" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Any distance</SelectItem>
                          {RADIUS_OPTIONS.map((r) => (
                            <SelectItem key={r.value} value={r.value.toString()}>Within {r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="mb-4 block">Facilities</Label>
                    <div className="space-y-3">
                      {[
                        { id: 'wifi', label: 'WiFi' },
                        { id: 'parking', label: 'Parking' },
                        { id: 'ac', label: 'Air Conditioning' },
                        { id: 'elevator', label: 'Elevator' },
                        { id: 'laundry', label: 'Laundry' },
                        { id: 'balcony', label: 'Balcony' },
                        { id: 'cctv', label: 'CCTV' },
                        { id: 'security', label: 'Security' },
                      ].map((facility) => (
                        <label key={facility.id} className="flex items-center gap-3 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={filters.facilities.includes(facility.id)}
                            onChange={(e) => {
                              const newFacilities = e.target.checked
                                ? [...filters.facilities, facility.id]
                                : filters.facilities.filter(f => f !== facility.id);
                              handleFilterChange('facilities', newFacilities);
                            }}
                            className="w-4 h-4 rounded accent-accent cursor-pointer"
                          />
                          <span className="text-slate-700 group-hover:text-slate-900 text-sm md:text-base lg:text-base">{facility.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="mb-4 block">Tenant Type</Label>
                    <Select value={filters.tenantType} onValueChange={(v) => handleFilterChange('tenantType', v)}>
                      <SelectTrigger><SelectValue placeholder="Any tenant type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Any tenant type</SelectItem>
                        <SelectItem value="anyone">Anyone</SelectItem>
                        <SelectItem value="students">Students</SelectItem>
                        <SelectItem value="professionals">Professionals</SelectItem>
                        <SelectItem value="families">Families</SelectItem>
                        <SelectItem value="couples">Couples</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="verified">Verified Owners Only</Label>
                    <Switch id="verified" checked={filters.verifiedOnly} onCheckedChange={(v) => handleFilterChange('verifiedOnly', v)} />
                  </div>
                  <SheetClose asChild>
                    <Button variant="outline" className="w-full rounded-full" onClick={clearFilters}>
                      <Trash2 className="w-4 h-4 mr-2" /> Clear All Filters
                    </Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>

            {/* View Mode Toggle */}
            <div className="hidden lg:flex items-center gap-1 bg-slate-100 rounded-full p-1">
              {[
                { mode: 'split', icon: <><div className="w-3 h-5 bg-current rounded-sm opacity-60" /><div className="w-3 h-5 bg-current rounded-sm" /></>, testId: 'view-split-btn' },
                { mode: 'list', icon: <List className="w-5 h-5" />, testId: 'view-list-btn' },
                { mode: 'map', icon: <MapIcon className="w-5 h-5" />, testId: 'view-map-btn' },
              ].map(({ mode, icon, testId }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`p-2 rounded-full transition-colors ${viewMode === mode ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
                  data-testid={testId}
                >
                  <div className={mode === 'split' ? 'flex gap-0.5' : ''}>{icon}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-32 h-screen flex">
        {/* Property List */}
        {(viewMode === 'split' || viewMode === 'list') && (
          <div className={`overflow-y-auto bg-white ${viewMode === 'split' ? 'w-2/5 border-r border-slate-200' : 'w-full'}`}>
            <div className="p-6">
              {backendOffline && (
                <div
                  role="status"
                  className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  data-testid="backend-offline-banner"
                >
                  <RefreshCw className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                  <div className="flex-1">
                    <div className="font-semibold">Showing sample listings</div>
                    <div className="text-amber-800/90">
                      We couldn't reach our servers, so you're seeing demo
                      properties to preview the experience. Live results will
                      return when the connection is restored.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => fetchProperties()}
                    className="shrink-0 rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 transition"
                  >
                    Retry
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mb-6">
                <p className="text-base md:text-base lg:text-base text-slate-600">
                  <span className="font-bold text-slate-900">{total}</span> properties found
                </p>
                {userLocation && (
                  <Badge variant="outline" className="text-accent border-accent/30">
                    <Navigation className="w-3 h-3 mr-1" /> Near you
                  </Badge>
                )}
              </div>

              {loading ? (
                <div className="grid gap-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="rounded-2xl overflow-hidden">
                      <Skeleton className="aspect-[4/3] w-full" />
                      <div className="p-4 space-y-2"><Skeleton className="h-6 w-3/4" /><Skeleton className="h-4 w-1/2" /></div>
                    </div>
                  ))}
                </div>
              ) : properties.length === 0 ? (
                <div className="text-center py-16">
                  <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-serif text-xl md:text-2xl lg:text-2xl text-slate-900 mb-2">No properties found</h3>
                  <p className="text-base md:text-base lg:text-base text-slate-500 mb-6">Try adjusting your filters or search area</p>
                  <Button onClick={clearFilters} variant="outline" className="rounded-full">Clear Filters</Button>
                </div>
              ) : (
                <div className={`grid gap-6 ${viewMode === 'list' ? 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : ''}`}>
                  {properties.map((property) => (
                    <div
                      key={property.property_id}
                      id={`property-${property.property_id}`}
                      className={`transition-all duration-200 ${selectedProperty === property.property_id ? 'ring-2 ring-accent rounded-2xl' : ''}`}
                      onMouseEnter={() => handlePropertyHover(property.property_id)}
                      onMouseLeave={() => handlePropertyHover(null)}
                    >
                      <PropertyCard property={property} viewerCurrency={viewerCurrency} showDistance={!!userLocation} />
                    </div>
                  ))}
                </div>
              )}

              {total > 20 && (
                <div className="flex justify-center gap-2 mt-8">
                  <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full">Previous</Button>
                  <span className="flex items-center px-4 text-slate-600">Page {page} of {Math.ceil(total / 20)}</span>
                  <Button variant="outline" onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / 20)} className="rounded-full">Next</Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Map */}
        {(viewMode === 'split' || viewMode === 'map') && (
          <div className={`relative ${viewMode === 'split' ? 'w-3/5' : 'w-full'}`}>
            {!hasValidApiKey || loadError ? (
              <MapPlaceholder markers={markers} onMarkerClick={handleMarkerClick} selectedProperty={selectedProperty} hoveredProperty={hoveredProperty} />
            ) : isLoaded ? (
              <>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={mapZoom}
                  options={mapOptions}
                  onLoad={handleMapLoad}
                  onIdle={handleBoundsChanged}
                >
                  {/* User Location Blue Dot */}
                  {userLocation && (
                    <OverlayView
                      position={{ lat: userLocation.latitude, lng: userLocation.longitude }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      getPixelPositionOffset={() => ({ x: -10, y: -10 })}
                    >
                      <div className="relative" data-testid="user-location-marker">
                        <div className="w-5 h-5 bg-blue-500 rounded-full border-[3px] border-white shadow-lg" />
                        <div className="absolute inset-0 w-5 h-5 bg-blue-400 rounded-full animate-ping opacity-40" />
                      </div>
                    </OverlayView>
                  )}

                  {/* Property Price Markers (shown when not too many) */}
                  {showIndividualMarkers && markers.map((marker) => (
                    <PriceMarkerOverlay
                      key={marker.property_id}
                      position={{ lat: marker.latitude, lng: marker.longitude }}
                      text={formatMarkerPrice(marker.monthly_rent, marker.currency)}
                      isActive={selectedProperty === marker.property_id || hoveredProperty === marker.property_id}
                      onClick={() => handleMarkerClick(marker)}
                      propertyId={marker.property_id}
                      verified={marker.owner_verified}
                    />
                  ))}
                </GoogleMap>

                {/* Search This Area Button */}
                {showSearchArea && (
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 animate-in fade-in slide-in-from-top-2 duration-300">
                    <Button
                      onClick={handleSearchThisArea}
                      className="bg-white text-slate-900 hover:bg-slate-50 rounded-full shadow-lg border border-slate-200"
                      disabled={mapLoading}
                      data-testid="search-this-area-btn"
                    >
                      {mapLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Search This Area
                    </Button>
                  </div>
                )}

                {/* Location Button */}
                <div className="absolute bottom-6 right-4 z-10">
                  <Button
                    onClick={handleCenterOnUser}
                    size="icon"
                    className={`w-12 h-12 rounded-full shadow-lg ${
                      userLocation ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-white hover:bg-slate-50 text-slate-700'
                    }`}
                    disabled={locationLoading}
                    data-testid="center-location-btn"
                  >
                    {locationLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Navigation className="w-5 h-5" />}
                  </Button>
                </div>

                {/* Map Loading Overlay */}
                {mapLoading && (
                  <div className="absolute inset-0 bg-white/20 flex items-center justify-center pointer-events-none">
                    <div className="bg-white rounded-full p-3 shadow-lg">
                      <Loader2 className="w-6 h-6 text-accent animate-spin" />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-100">
                <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile View Toggle */}
      <div className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
        <div className="flex items-center gap-1 bg-white rounded-full p-1 shadow-lg border border-slate-200">
          <button onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}>
            <List className="w-4 h-4 inline mr-1" /> List
          </button>
          <button onClick={() => setViewMode('map')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${viewMode === 'map' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}>
            <MapIcon className="w-4 h-4 inline mr-1" /> Map
          </button>
        </div>
      </div>
    </div>
  );
}
